<template>
	<ModalHeader
		:title="title"
		:active="item.deepen"
		:button-text="getString('save')"
		withSwitch
		@switched="(v) => item.deepen = v"
		@click-button="clickSave" />
	<div class="deepen-container">
		<div class="deepen-data row">
			<div class="column-6 left">
				<div class="row">
					<div class="column-6 column-part left-column">
						<div class="row slug-input-container">
							<AppTextInput ref="slug-input" :label="getString('product_url')" :placeholder="getString('product_url_placeholder')" :value="item.slug ? item.slug : ''" @changing-text="sanitizeSlug" @focusout="setUniqueSlug" />
							<AppInPlaceCloud @close-cloud="closeSlugCloud" :type="-2" :active="activeWarningCloud">
								<p>{{ getString('product_url_warn') }}</p>
							</AppInPlaceCloud>
						</div>
					</div>
					<div class="column-4 column-part right-column">
						<AppSwitch :label="getString('product_notes')" :checked="item.note" @switched="(v) => item.note = v" />
					</div>
					<div class="column-2 column-part right-column">
						<AppColorPicker :label="getString('color')" :value="item.color ? '#'+item.color.replace('#', '') : ''" @changed-color="(v) => item.color = '#'+v.replace('#', '')" />
					</div>
				</div>
				<div class="row mt-20">
					<div class="column-12">
						<AppDropzone
							:max-bytes="600 * 1024"
							:max-width="1024"
							:max-height="1024"
							:max-files="10"
							:label="getString('extra_images')"
							:files="images"
							@upload-complete="picturesChanged"
							@removed-file="(url, files) => picturesChanged(files)" />
					</div>
				</div>
			</div>
			<div class="column-6 right">
				<div class="row">
					<div class="column-12">
						<AppTextInput class="text-deepen" :label="getString('long_description')" :placeholder="getString('product_description')" :value="item.long_description ? item.long_description : ''" textarea @changed-text="(v) => item.long_description = v" />
						<AppTextInput class="text-deepen mt-20" :label="userStore.ingredientsWord" :placeholder="getString('product_description')" :value="item.materials ? item.materials : ''" textarea @changed-text="(v) => item.materials = v" />
						<AppTextInput class="text-deepen mt-20" :label="getString('youtube_embed')" :placeholder="getString('youtube_embed')" :value="item.video_link ? item.video_link : ''" @changed-text="(v) => item.video_link = v" />
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import AppSwitch from "@/shared/components/AppSwitch.vue";
import AppInPlaceCloud from "@/shared/components/AppInPlaceCloud.vue";
import AppColorPicker from "@/shared/components/AppColorPicker.vue";
import AppDropzone from "@/shared/components/AppDropzone.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";

import {useLanguageStore, useNotificationStore} from "@/shared/stores";
import {useMenuStore, useUserStore} from "@/console-new/stores";

export default {
	setup() {
		let languageStore = useLanguageStore();
		let notificationStore = useNotificationStore();
		let menuStore = useMenuStore();
		let userStore = useUserStore();
		return {getString: languageStore.getString, menuStore, userStore, showNotification: notificationStore.showNotification};
	},
	emits: ["save-clicked"],
	name: "DeepenEditor",
	data() {
		return {
			activeWarningCloud: false,
			warningCloudTimeout: null,
		};
	},
	components: {AppColorPicker, AppSwitch, AppDropzone, AppTextInput, ModalHeader, AppInPlaceCloud},
	props: {
		itemTempId: {
			type: String,
			required: true
		},
		categoryTempId: {
			type: String,
			required: true
		}
	},
	computed: {
		title() {
			return this.getString("deepen_title", [this.item.name]);
		},
		item() {
			return this.menuStore.modItemFromTempIds(this.categoryTempId, this.itemTempId);
		},
		images() {
			return this.item.pictures.map(picture => {
				return picture.url;
			});
		}
	},
	methods: {
		picturesChanged(files) {
			let newPictures = [];
			let i = 0;
			files.forEach(file => {
				let existingPicture = this.item.pictures.find(picture => picture.url === file);
				if(existingPicture) {
					newPictures.push({
						menu_item_image_id: existingPicture.menu_item_image_id,
						url: file,
						sort_number: i
					});
				} else {
					newPictures.push({
						url: file,
						sort_number: i
					});
				}
				i++;
			});
			this.item.pictures = newPictures;
		},
		replaceCharacter(text) {
			text = (text || "").toLowerCase();
			text = text.replaceAll(" ", "-");
			let regexList = [/[áàãâä]/gi, /[éèêë]/gi, /[íìîï]/gi, /[óòôõö]/gi, /[úùûü]/gi, /[ç]/gi, /[ñ]/gi, /\s/gi, /_/gi, /@/gi, /&/gi, /[^a-z0-9\-]/gi];
			let replaceList = ["a", "e", "i", "o", "u", "c", "n", "-", "-", "a", "e", "-"];
			for (let i = 0; i < regexList.length; i++) {
				text = text.replace(regexList[i], replaceList[i]);
			}
			text = text.normalize("NFD").toLowerCase();
			return text || null;
		},
		sanitizeSlug(value) {
			let originalText = (value || "").toLowerCase();
			this.item.slug = this.replaceCharacter(value);
			if(this.item.slug && this.item.slug !== originalText) {
				this.activeWarningCloud = true;
				clearTimeout(this.warningCloudTimeout);
				this.warningCloudTimeout = setTimeout(() => {
					this.activeWarningCloud = false;
				}, 3000);
			}
		},
		setUniqueSlug() {
			this.sanitizeSlug(this.item.slug);
			this.item.slug = this.menuStore.getUniqueItemSlug(this.item.slug);
		},
		clickSave() {
			this.setUniqueSlug();
			if(!this.item.slug && this.item.deepen) {
				this.showNotification("deepen-empty-url-error");
			} else {
				this.$emit("save-clicked");
			}
		},
		closeSlugCloud() {
			this.activeWarningCloud = true;
			clearTimeout(this.warningCloudTimeout);
		}
	}
}
</script>

<style lang="scss" scoped>
.deepen-data {
	padding: 15px 10px;

	.column-12 {
		width: 100%;
	}

	.column-6 {
		width: 50%;
	}

	.column-part {
		display: flex;
		justify-content: center;
		align-items: flex-start;
	}

	.column-4 {
		width: 33.33333333333333%;
	}

	.column-2 {
		width: 16.66666666666667%;
	}

	.left-column {
		justify-content: flex-start;
	}

	.right-column {
		justify-content: flex-end;
	}

	.center-column {
		justify-content: center;
	}

	.text-deepen {
		width: 100%;
	}

	.dropzone-container {
		width: 100%;
	}

	& .left {
		padding-right: 15px;
	}

	@media (max-width: 768px) {
		width: 100%;
		.left {
			width: 100%;
			padding-right: 0;
			margin-bottom: 20px;
		}
		.right {
			width: 100%;
			padding-left: 0;
		}
	}

	:deep(.dropzone) {
		height: 100px;
	}

	.slug-input-container {
		position: relative;

		:deep(.in-place-cloud) {
			background-color: $bg_0;
			width: 100%;
			z-index: 1;
		}
	}

}
</style>